import { useEffect } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { i18n } from '@/i18n';
import { Skeleton } from '@/components/ui/skeleton';
import { EmptyState } from '@/components/ui/empty-state';
import {
  useDisruptionProgress,
  useResolutionState,
} from '@/lib/disruptions/use-disruptions';
import type { ResolutionState } from '@/lib/disruptions/types';
import { formatPriceMinor, isPriced } from './labels';
import { ResolutionRun } from './resolution-run';
import { ResolutionStepper } from './resolution-stepper';
import { ResolutionProblemsPanel } from './resolution-problems-panel';

// disruption-overview (epic NEX-376 / slice U1) — the RESOLUTION COCKPIT container.
//
// This is the shared Overview mount point (U2 fills the empty slots below). It reads
// the B1 `resolution-state@v1` aggregate ONCE (useResolutionState — WS not poll),
// renders the KPI tiles + the guided <ResolutionStepper>, and leaves two clearly-
// labelled empty slots for U2's activity + problems panels.
//
// REALTIME = WS, never a client timer. The detail route already joined the
// disruption Socket.IO room; the BFF pushes each fresh progress snapshot into the
// WS-backed `['disruptions', urn, 'progress']` cache (apply-event.ts, setQueryData).
// We read that same cache and invalidate the resolution-state aggregate whenever the
// run snapshot MOVES (runStatus / processed counts / rooms committed change) — so the
// cockpit re-reads off the WS push, with NO interval poll here.

interface DisruptionResolutionCockpitProps {
  disruptionUrn: string;
}

export function DisruptionResolutionCockpit({ disruptionUrn }: DisruptionResolutionCockpitProps) {
  const { t } = useTranslation('rebooking');
  const qc = useQueryClient();
  const { data, isLoading, isError } = useResolutionState(disruptionUrn);

  // WS-driven refresh (NOT a poll): the progress query is fed by the disruption WS
  // room (applyEvent → setQueryData). When its snapshot moves — run counters, the
  // booking allocation run, a sync fan-out's per-case progress, or the group facts
  // (assigned / ticketed / held / confirmed / accepted) — re-read the aggregate so
  // the rail, the button and the steps reflect the run live.
  const progress = useDisruptionProgress(disruptionUrn);
  const p = progress.data;
  const g = p?.groups && 'groups' in p.groups ? p.groups : null;
  const fingerprint = p
    ? [
        p.runStatus,
        p.processedGroups ?? 0,
        p.roomsCommitted ?? 0,
        p.resolved,
        p.optionsGenerated,
        p.activeRun?.op ?? '',
        p.activeRun?.runId ?? '',
        p.activeRun?.progress?.done ?? '',
        p.allocationRun?.status ?? '',
        p.allocationRun?.processedGroups ?? '',
        p.allocationRun?.heldGroups ?? '',
        g ? `${g.assigned}:${g.ticketed}:${g.hotelHeld}:${g.hotelConfirmed}:${g.voucherIssued}:${g.awaiting}:${g.accepted}:${g.declined}:${g.resolved}` : '',
      ].join('|')
    : null;
  useEffect(() => {
    if (!fingerprint) return;
    void qc.invalidateQueries({ queryKey: ['disruptions', disruptionUrn, 'resolution-state'] });
  }, [fingerprint, disruptionUrn, qc]);

  // A malformed aggregate (no kpis/steps/nextStep) is a failed read, not a crash:
  // render the same recoverable state instead of taking the whole route down.
  const malformed = !!data && (!data.kpis || !Array.isArray(data.steps) || !data.nextStep);
  if (isError || malformed) {
    return (
      <EmptyState
        title={t('cockpit.error.title')}
        description={t('cockpit.error.body')}
      />
    );
  }

  if (isLoading || !data) {
    return (
      <div className="flex flex-col gap-3" data-testid="resolution-cockpit-loading">
        <Skeleton className="h-6 w-2/3" />
        <Skeleton className="h-36 w-full" />
        <Skeleton className="h-48 w-full" />
      </div>
    );
  }

  const empty = data.kpis.caseCount === 0 && data.kpis.affectedPassengers === 0;

  // ONE viewport: header facts, the hero, then the stepper — the stepper's list is
  // the only thing that scrolls (contained), never the page.
  return (
    <div className="flex h-full min-h-0 flex-col gap-2.5" data-testid="resolution-cockpit">
      <FactsLine state={data} />

      {empty ? (
        <EmptyState
          title={t('cockpit.empty.title')}
          description={t('cockpit.empty.body')}
        />
      ) : (
        <>
          <ResolutionRun disruptionUrn={disruptionUrn} state={data} />
          <section aria-label={t('cockpit.guidedAria')} className="min-h-0 flex-1 overflow-y-auto pr-1">
            <ResolutionStepper disruptionUrn={disruptionUrn} state={data} />
            <div className="mt-2" data-testid="resolution-problems-slot">
              <ResolutionProblemsPanel state={data} disruptionUrn={disruptionUrn} />
            </div>
          </section>
        </>
      )}
    </div>
  );
}

/** One line of facts (replaces the 5-tile grid): flights · PNR · pax · hotel price split.
 *  airline-money@v2 — every amount is the airline's PRICE; null → «No disponible». */
function FactsLine({ state }: { state: ResolutionState }) {
  const { t } = useTranslation('rebooking');
  const { kpis } = state;
  const g = kpis.groups;
  const price = formatPriceMinor(kpis.totalPriceMinor, kpis.currency, t);
  // The hotels step is SKIPPED only once the rebooking settled AND the facts say
  // nobody overnights — the one case where "$0" must read as a fact, not a gap.
  const noHotelNeeded = state.steps.some((s) => s.op === 'hotels' && s.status === 'SKIPPED');
  return (
    <p
      className="flex flex-wrap items-center gap-x-3 gap-y-1 text-sm text-muted-foreground"
      data-testid="resolution-facts"
    >
      <span>
        <b className="text-foreground">{kpis.caseCount}</b>{' '}
        {t('cockpit.facts.flights', { count: kpis.caseCount })}
      </span>
      <span>·</span>
      <span>
        <b className="text-foreground">{g ? g.groups : '—'}</b> PNR
      </span>
      <span>·</span>
      <span>
        <b className="text-foreground">{kpis.affectedPassengers}</b>{' '}
        {t('cockpit.facts.passengers', { count: kpis.affectedPassengers })}
      </span>
      <span>·</span>
      {noHotelNeeded ? (
        <span data-testid="resolution-facts-no-hotel">
          {t('cockpit.facts.hotelsLabel')}{' '}
          <b className="text-foreground">{t('cockpit.facts.notNeeded')}</b>
          <span className="ml-1 text-xs">{t('cockpit.facts.noHotelHint')}</span>
        </span>
      ) : (
        <span title={priceHint(kpis)} data-testid="resolution-facts-price">
          {kpis.priceSource === 'actual+estimate'
            ? t('cockpit.facts.hotelPrice')
            : t('cockpit.facts.estimatedPrice')}{' '}
          <b className="text-foreground">{price}</b>
          {kpis.priceSource === 'actual+estimate' && isPriced(kpis.totalPriceMinor) ? (
            <span className="ml-1 text-xs">({priceHint(kpis)})</span>
          ) : null}
        </span>
      )}
    </p>
  );
}

/**
 * "booked $X · held $Y · est. $Z" — which part of the PRICE is committed money and which
 * is a forecast (airline-money@v2: every part already priced server-side). Not
 * available (no price card / only the unpriceable S4 estimate) → says so.
 */
export function priceHint(k: {
  priceSource?: 'actual+estimate' | 'estimate';
  totalPriceMinor?: number | null;
  bookedPriceMinor?: number | null;
  heldPriceMinor?: number | null;
  estimatedPriceMinor?: number | null;
  currency: string;
}): string | undefined {
  // Exported and rendered by surfaces outside this file (price tab, pulse bridge), so
  // it reads the live instance instead of taking a `t` the callers would have to pass.
  if (!isPriced(k.totalPriceMinor)) return i18n.t('disruptions:price.unavailableHint');
  if (k.priceSource !== 'actual+estimate') return i18n.t('rebooking:cockpit.price.estimateHint');
  const parts: string[] = [];
  const fmt = (v: number) => formatPriceMinor(v, k.currency, i18n.t);
  if (isPriced(k.bookedPriceMinor) && k.bookedPriceMinor > 0)
    parts.push(i18n.t('rebooking:cockpit.price.booked', { value: fmt(k.bookedPriceMinor) }));
  if (isPriced(k.heldPriceMinor) && k.heldPriceMinor > 0)
    parts.push(i18n.t('rebooking:cockpit.price.held', { value: fmt(k.heldPriceMinor) }));
  if (isPriced(k.estimatedPriceMinor) && k.estimatedPriceMinor > 0)
    parts.push(i18n.t('rebooking:cockpit.price.estPending', { value: fmt(k.estimatedPriceMinor) }));
  return parts.length > 0 ? parts.join(' · ') : i18n.t('rebooking:cockpit.price.none');
}
